import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useFolders } from '@/api/folders';
import { useBulkMoveFiles } from '@/api/files';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import { cn } from '@/lib/cn';

interface Props {
  open: boolean;
  onClose: () => void;
  fileIds: string[];
  /** The folder the files currently live in — excluded from the picker. */
  currentFolderId: string;
  workspaceId: string;
  /** Called after a successful move so the parent can clear selection. */
  onMoved?: (result: { moved: number; failed: number }) => void;
}

export function BulkMoveModal({
  open,
  onClose,
  fileIds,
  currentFolderId,
  workspaceId,
  onMoved,
}: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const folders = useFolders(workspaceId);
  const move = useBulkMoveFiles();
  const toast = useToast();

  const handleSubmit = () => {
    if (!selectedId || fileIds.length === 0) return;
    move.mutate(
      {
        file_ids: fileIds,
        folder_id: selectedId,
        prev_folder_id: currentFolderId,
      },
      {
        onSuccess: (result) => {
          if (result.failed > 0) {
            toast.warning(
              `Moved ${result.moved} file${result.moved === 1 ? '' : 's'}`,
              `${result.failed} could not be moved.`,
            );
          } else {
            toast.success(`Moved ${result.moved} file${result.moved === 1 ? '' : 's'}`);
          }
          onMoved?.(result);
          onClose();
        },
        onError: (e) => toast.error('Move failed', apiError(e)),
      },
    );
  };

  const available = (folders.data ?? []).filter((f) => f.id !== currentFolderId);
  const count = fileIds.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Move files to folder"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={move.isPending}
            disabled={!selectedId || count === 0}
          >
            Move {count} file{count === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-slate-600 mb-3">
        Select a destination folder for{' '}
        <span className="font-medium">
          {count} file{count === 1 ? '' : 's'}
        </span>
        .
      </p>
      {folders.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : available.length === 0 ? (
        <p className="text-sm text-slate-500">No other folders available.</p>
      ) : (
        <ul className="border border-bb-lilac divide-y divide-slate-100 max-h-60 overflow-y-auto custom-scrollbar">
          {available.map((folder) => (
            <li key={folder.id}>
              <button
                type="button"
                onClick={() => setSelectedId(folder.id)}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm hover:bg-slate-50',
                  selectedId === folder.id
                    ? 'bg-bb-navy/5 text-bb-navy font-medium'
                    : 'text-slate-700',
                )}
              >
                {folder.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
